import ActionType from "../actions/ActionType"
import { IAction } from "../actions/Action"
import { ICapability, IGetProject, IGetProjectPath } from '../model/IGetProject';
import { IGetDataset } from '../model/IGetDataset';
import { parseTestRunNameToTimestep, parseToTime } from "../helpers/fixTime";
import { TESTRUNSUBFOLDER } from "../shared/constants";
import { IAxis, IChartItem, IInitialResultChartData } from "../model/IInitialChartData";
import { IChartBullet } from "../model/IChartData";

export interface ITestRunFolder {
  name: string;
  id: string; 
}
export interface IProjectContentState {
  loadingProjectAndParent: boolean;
  loadingProjectContent: boolean;
  projectContentDialogIsOpen: boolean;
  projectContentDialogIsSelectable: boolean;
  project: IGetProject | null;  
  projectContent: Array<IGetProject | IGetDataset>;
  currentNavigationProject: IGetProject;
  currentNavigationParentProject: IGetProject;
  currentNavigationProjectPath: IGetProjectPath[];
  currentNavigationProjectCapability: ICapability | null;
  page: number;
  rowsPerPage: number;
  order: 'asc' | 'desc';
  orderBy: string | ((item: any) => string | number)
  filter: string;  
  hasModelResults: boolean;
  modelResultsUpdatedOn: string;
  testRunFolders: Array<ITestRunFolder>;
  loadingTestRunFolders: boolean;
  selectedTestRunFolder: ITestRunFolder | null;
  loadingTestRunMapLayer: boolean;
  resultInitialChartData: IInitialResultChartData | null;
  loadingResultInitialChartData: boolean;
  selectedResultPointName: string;
  selectedChartXItem: IChartItem | null;
  selectedChartYItem: IChartItem | null;
  chartAxisRange: any;
  chartBullets: Array<IChartBullet>
}

const initState = {
  loadingProjectAndParent: false,
  loadingProjectContent: false,
  project: null,   
  projectContent: Array<IGetProject | IGetDataset>(),
  currentNavigationProject: null,
  currentNavigationParentProject: null,
  currentNavigationProjectPath: Array<IGetProjectPath>(),
  currentNavigationProjectCapability: null,
  projectContentDialogIsOpen: false,
  projectContentDialogIsSelectable: false,
  page: 0,
  rowsPerPage: 10,
  order: 'asc',
  orderBy: '',  
  filter: "",
  hasModelResults: false,
  modelResultsUpdatedOn: "",
  testRunFolders: Array<ITestRunFolder>(),
  loadingTestRunFolders: false,
  selectedTestRunFolder: null,
  loadingTestRunMapLayer: false,
  resultInitialChartData:  null,
  loadingResultInitialChartData: false,
  selectedResultPointName: "",
  selectedChartXItem: null,
  selectedChartYItem: null,
  chartAxisRange: null,
  chartBullets: Array<IChartBullet>(),
}

const projectContent = (state = initState, action: IAction) => {
  switch (action.type) {
    case ActionType.RESULT_CHART_DATA_SET:{
      const {axises, dataX, dataY } = action.data 
      const initialData: IInitialResultChartData = state.resultInitialChartData
      const timeSteps = initialData.dataX
      const tId = initialData.axises[0].id
      const xId = axises[0].id 
      const yId = axises[1].id 
      const xIsTime = xId === tId  
      const yIsTime = yId === tId      
      const bullets = dataX.map((d: any, index: number) => {
        const timeStep = timeSteps[index]
        const timeInMilliseconds = parseToTime(timeStep.toString())
        return {
          id: index,
          x: xIsTime ? timeInMilliseconds : d,
          y: yIsTime ? timeInMilliseconds : dataY[index],
          isSelected: false, isSelectedForTestRun: false, Time: timeStep
        }
      })     
      return { ...state, chartBullets: bullets, loadingResultInitialChartData: false }   
    }
    case ActionType.SET_RESULT_INITIAL_CHART_DATA:{
      const {parameters, axises, dataX, dataY } = action.data 
      if (parameters && parameters.length > 1 && axises && axises.length === 2){        
        const timeItemId = axises[0].id
        const previewItemId = axises[1].id
        const x = parameters && timeItemId ? parameters.find(d => d.id === timeItemId): null
        const y = parameters && previewItemId ? parameters.find(d => d.id === previewItemId): null
        let bullets = Array<IChartBullet>()
        if (x && y){
          bullets = dataX.map((d: any, index: number) => {
            return {
              id: index,
              x: parseToTime(d),
              y: dataY[index],
              isSelected: false, isSelectedForTestRun: false, Time: d
            }
          })
        }   
        return { ...state, chartBullets: bullets, resultInitialChartData: action.data, loadingResultInitialChartData: false, selectedChartXItem: x, selectedChartYItem: y }
      }
         
      return { ...state, chartBullets: new Array<IChartBullet>(), initialChartData: {
        axises: new Array<IAxis>(), 
        items: new Array<IChartItem>(), 
        data: []
      }, loadingChartData: false, selectedChartXItem: null, selectedChartYItem: null }
      
    }
    case ActionType.RESULT_CHART_DATA_GET:  
    return { ...state, selectedChartXItem: action.data.xItem, selectedChartYItem: action.data.yItem } 
    case ActionType.RESULT_CHART_AXIS_RANGE_SET:
      return {...state, chartAxisRange: action.data}
    case ActionType.SET_SELECTED_RESULT_POINT_NAME: 
      return {...state, selectedResultPointName: action.data}
    case ActionType.LOADING_RESULT_INITIAL_CHART_DATA:
      return {...state, loadingResultInitialChartData: action.data}   
    case ActionType.LOADING_TEST_RUN_MAP_LAYER:
      return {...state, loadingTestRunMapLayer: action.data}
    case ActionType.CLEAR_RESULTS:       
      return {...state, 
        hasModelResults: false, 
        modelResultsUpdatedOn: "", 
        testRunFolders: Array<ITestRunFolder>(), 
        loadingTestRunFolders: false,
        selectedTestRunFolder: null,
        loadingTestRunMapLayer: false,
        resultInitialChartData:  null,
        loadingResultInitialChartData: false,
        selectedResultPointName: ""
      }
    case ActionType.SET_HAS_MODEL_RESULTS:{
      const { hasResults, updatedOn, testRunFolders } = action.data      
      const tf = testRunFolders.map((f: IGetProject) => {
        return {
           name: parseTestRunNameToTimestep(f.name.replace(TESTRUNSUBFOLDER, "")),
           id: f.id
        }
      })
      return {...state, hasModelResults: hasResults, modelResultsUpdatedOn: updatedOn, testRunFolders: tf}
    } 
    case ActionType.SET_SELECTED_TEST_RUN_FOLDER: {
      return {...state, selectedTestRunFolder: action.data}
    }
    case ActionType.LOADING_TEST_RUN_FOLDERS : {
      return {...state, loadingTestRunFolders: action.data}
    } 
    case ActionType.OPEN_PROJECTCONTENT_DIALOG:
      return { ...state, projectContentDialogIsOpen: action.data.open, projectContentDialogIsSelectable: action.data.selectable }; 
    case ActionType.LOADING_PROJECT:
      return { ...state, loadingProjectAndParent: action.data }; 
    case ActionType.FILTER_SET:
      return { ...state, filter: action.data }; 
    case ActionType.LOADING_PROJECTCONTENT:
      return { ...state, loadingProjectContent: action.data };    
    case ActionType.SET_PROJECTCONTENT:
      {     
        return {
          ...state,     
          projectContent: action.data.projectContent,
          currentNavigationParentProject: action.data.parent,
          currentNavigationProject: action.data.project,
          currentNavigationProjectPath: action.data.path,
          currentNavigationProjectCapability: action.data.capabilities,
          loadingProjectContent: false
        };   
      }      
    case ActionType.SET_PROJECT:{
      const { project, projectPath } = action.data
        return {
          ...state,
          project,
          projectPath,           
          loadingProjectAndParent: false
        };
      }
      
      case ActionType.SET_PROJECTCONTENT_PAGE:
        return {
          ...state,
          page: action.data
        };
      case ActionType.SET_PROJECTCONTENT_ROWSPERPAGE:
        return {
          ...state,
          rowsPerPage: action.data
        };
      case ActionType.SET_PROJECTCONTENT_ORDER:
        return {
          ...state,
          order: action.data
        };
      case ActionType.SET_PROJECTCONTENT_ORDERBY:
        return {
          ...state,
          orderBy: action.data
        };     
  
    default:
        return state;
  }
};

export default projectContent